import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";
import { Loader2, MessageSquare, Plus, User, Clock, Pin, Image, X } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["general", "introductions", "filmmaking", "technology", "festivals", "feedback"];

export default function Forum() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", content: "", category: "general", image_url: null });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const url = selectedCategory === "all" 
        ? `${API}/forum/posts` 
        : `${API}/forum/posts?category=${selectedCategory}`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, GIF, or WebP");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/forum/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewPost({ ...newPost, image_url: response.data.url });
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setNewPost({ ...newPost, image_url: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreatePost = async () => {
    if (!user) {
      toast.error("Please sign in to create a post");
      navigate("/login");
      return;
    }

    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/posts`, newPost, { withCredentials: true });
      toast.success("Post created successfully!");
      setIsCreateOpen(false);
      setNewPost({ title: "", content: "", category: "general", image_url: null });
      setImagePreview(null);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
              Community
            </p>
            <h1 className="font-syne text-4xl md:text-5xl font-bold text-white">
              Forum
            </h1>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-post-btn"
                className="bg-purple hover:bg-purple-hover text-white rounded-full px-6"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-void-surface border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">Create New Post</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Title</label>
                  <Input
                    value={newPost.title}
                    onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                    placeholder="Enter post title..."
                    data-testid="post-title-input"
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Category</label>
                  <select
                    value={newPost.category}
                    onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                    data-testid="post-category-select"
                    className="w-full px-3 py-2 bg-void border border-white/10 text-white rounded-none"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat} className="bg-void-surface">
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Content</label>
                  <Textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                    placeholder="Write your post..."
                    rows={5}
                    data-testid="post-content-input"
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                
                {/* Image Upload */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Image (optional)</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg border border-white/10"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                        title="Remove image"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      {uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full p-6 border-2 border-dashed border-white/20 hover:border-purple/50 rounded-lg transition-colors flex flex-col items-center gap-2 text-slate-400 hover:text-slate-300"
                    >
                      {uploading ? (
                        <Loader2 className="w-8 h-8 animate-spin" />
                      ) : (
                        <>
                          <Image className="w-8 h-8" />
                          <span className="text-sm">Click to upload image</span>
                          <span className="text-xs text-slate-500">JPG, PNG, GIF, WebP (max 5MB)</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                <Button
                  onClick={handleCreatePost}
                  disabled={submitting || uploading}
                  data-testid="submit-post-btn"
                  className="w-full bg-purple hover:bg-purple-hover text-white"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Create Post"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setSelectedCategory("all")}
            data-testid="filter-all"
            className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${
              selectedCategory === "all"
                ? "bg-purple text-white"
                : "bg-void-surface text-slate-400 hover:text-white border border-white/10"
            }`}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              data-testid={`filter-${category}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${
                selectedCategory === category
                  ? "bg-purple text-white"
                  : "bg-void-surface text-slate-400 hover:text-white border border-white/10"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No posts yet. Be the first to start a discussion!</p>
            {!user && (
              <Link to="/login" className="text-purple-light hover:text-white">
                Sign in to create a post
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/forum/${post.id}`}
                  data-testid={`forum-post-${index}`}
                  className={`block p-6 bg-void-surface border transition-all duration-500 group ${
                    post.pinned 
                      ? "border-purple/40 bg-purple/5" 
                      : "border-white/5 hover:border-purple/30"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-purple/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-light" />
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {post.pinned && (
                          <span className="flex items-center gap-1 text-xs text-purple-light bg-purple/20 px-2 py-0.5">
                            <Pin className="w-3 h-3" />
                            Pinned
                          </span>
                        )}
                        <span className="text-xs uppercase tracking-wider text-purple-light font-mono">
                          {post.category}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="font-syne font-bold text-white text-lg mb-2 group-hover:text-purple-light transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-slate-400 line-clamp-2 text-sm">{post.content}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                        <span>By {post.author_name}</span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {post.replies_count} replies
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
